import type { ErrorSchema } from "@rjsf/core"
import { Title, Markdown, OutlinedButton, Text, Checkbox } from "dash-styled"
import { SchemaFormRefProps } from "resha"
import { ReactComponent as ToolIcon } from "icons/lib/tool.svg"
import { SchemaForm as SchemaFormType } from "universal-schema/lib"

import { DocumentExtractionData } from "../../../../../Models/DataProgram/DocumentExtraction"
import {
    InputFormRender,
    InputFormRenderProps
} from "../../../../Common/InputFormRender/InputFormRender"
import { LinkButton } from "../../../../Common/LinkButton/LinkButton"
import { DocumentsList } from "./DocumentsList"

type SimpleEditorProps = {
    settings?: boolean
    demo?: {
        active: boolean
        toggle: () => void
    }
    input?: InputFormRenderProps & React.RefAttributes<SchemaFormRefProps>
    output: Omit<InputFormRenderProps, "schema" | "uiSchema"> &
        React.RefAttributes<SchemaFormRefProps>
    parameterSchema: SchemaFormType | undefined
    errors?: ErrorSchema
    onBack?: () => void
    openVisualEditor: () => void
}

export const SimpleEditor = ({
    settings,
    demo,
    input,
    output,
    parameterSchema,
    errors,
    onBack,
    openVisualEditor
}: SimpleEditorProps) => {
    const handleChange = (data: DocumentExtractionData) => {
        output.onChange?.(data, [])
    }

    return (
        <div className="flex w-full pb-4">
            {input && (
                <div className="w-full md:w-1/4 px-4 mt-14">
                    <div
                        data-testid="input-data"
                        className="border-gray-300 border-solid border rounded-lg px-4 py-2"
                    >
                        <div className="flex w-full justify-between py-4">
                            <Title
                                noMargin
                                component="h2"
                                variant="xl"
                                weight="semibold"
                                color="darkviolet"
                            >
                                Input data
                            </Title>
                            {onBack && (
                                <LinkButton
                                    testId="set-params-back"
                                    className="text-xs"
                                    onClick={onBack}
                                >
                                    Change
                                </LinkButton>
                            )}
                        </div>
                        <InputFormRender skipMissingDataField {...input} />
                    </div>
                </div>
            )}
            <div
                className={`flex flex-col px-4 w-full ${
                    input ? "md:w-3/4 lg:w-3/4 xl:w-3/4" : ""
                }`}
            >
                {parameterSchema !== undefined ? (
                    <>
                        <div className="flex justify-between items-start">
                            <div>
                                <Title
                                    color="darkviolet"
                                    variant={settings ? "heading2" : "heading1"}
                                    weight="semibold"
                                    component={settings ? "h3" : "h1"}
                                >
                                    {parameterSchema.uiSchema["ui:title"] ??
                                        parameterSchema.schema["title"] ??
                                        ""}
                                </Title>
                                {parameterSchema.schema?.description && (
                                    <Markdown
                                        className="text-sm text-smoke-500 mb-2"
                                        text={
                                            parameterSchema.schema?.description
                                        }
                                    />
                                )}
                            </div>
                            <OutlinedButton
                                label="Visual Editor"
                                Icon={ToolIcon}
                                className="bg-grape-500 hover:bg-grape-300 text-white rounded-lg py-3"
                                onClick={openVisualEditor}
                            />
                        </div>
                        <DocumentsList
                            schema={parameterSchema.schema}
                            formData={output.formData as DocumentExtractionData}
                            errors={errors}
                            onChange={handleChange}
                        />
                    </>
                ) : (
                    <Text>
                        Data program has no parameters, please proceed to the
                        next step.
                    </Text>
                )}
                {demo && (
                    <div className="flex items-center justify-center">
                        <Checkbox
                            variant="modern"
                            className="text-rhino-900"
                            label="I will process myself (demo mode)"
                            onChange={demo.toggle}
                            isChecked={demo.active}
                            testId="demo-mode-checkbox"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
